import { ArrowUpRight } from "lucide-react";

const JERSEY = "/products/jersey.png";
const TEE = "/products/tee.png";
const HELMET = "/products/helmet.png";

export default function Products({ onCta }) {
  return (
    <section
      id="gear"
      data-testid="products-section"
      className="relative bg-[#0a0a0a] py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#e11d2a] mb-5">
              <span className="inline-block w-8 h-px bg-[#e11d2a]" />
              <span>The Drop · 01</span>
            </div>
            <h2 className="font-anton uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-tight">
              Wear the
              <br />
              <span className="text-[#e11d2a]">Energy.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-neutral-400 text-base md:text-lg leading-relaxed">
              Two pieces. One squad. Designed for the kids who run hard, hit
              clean, and bring the noise every shift. Limited team run — your
              size, your name, your number.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Jersey big card */}
          <ProductCard
            id="product-jersey"
            badge="Game Jersey"
            tag="Custom · Numbered"
            title="The Patriot Jersey"
            subtitle="Navy mesh. Red raglan sleeves. Your name and number across the back."
            price="$58"
            img={JERSEY}
            className="md:col-span-7 md:row-span-2 min-h-[520px] md:min-h-[640px]"
            onCta={onCta}
            featured
          />

          {/* Tee card */}
          <ProductCard
            id="product-tee"
            badge="Squad Tee"
            tag="Heavyweight Cotton"
            title="The BSE Graphic Tee"
            subtitle="Off-field uniform. Wear it to school. Wear it to practice."
            price="$32"
            img={TEE}
            className="md:col-span-5 min-h-[320px] md:min-h-[300px]"
            onCta={onCta}
          />

          {/* Helmet card */}
          <ProductCard
            id="product-helmet"
            badge="Game Helmet"
            tag="Side Crest · Center Stripe"
            title="The Liberty Helmet"
            subtitle="Navy shell. Red cage. White center stripe. Crest on both sides."
            price="$129"
            img={HELMET}
            className="md:col-span-5 min-h-[300px]"
            onCta={onCta}
          />
        </div>

        {/* Spec strip */}
        <div className="mt-6 md:mt-8 bg-[#141414] border border-neutral-900 p-8 md:p-10 grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8 items-center">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2">
              Built To Last
            </div>
            <h3 className="font-anton text-2xl md:text-3xl uppercase tracking-tight leading-tight">
              Field-Tested.
              <br />
              Grass-Stain Proof.
            </h3>
          </div>
          {[
            "Sizes Youth S — Adult M",
            "Sweat-wicking mesh",
            "Screen-printed numbers",
            "Free name customization",
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3 text-sm text-neutral-300">
              <span className="w-1.5 h-1.5 bg-[#e11d2a] shrink-0" />
              <span className="tracking-wide">{f}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductCard({
  id,
  badge,
  tag,
  title,
  subtitle,
  price,
  img,
  className = "",
  onCta,
  featured = false,
}) {
  return (
    <article
      data-testid={id}
      className={`group relative overflow-hidden bg-[#141414] border border-neutral-900 hover:border-[#e11d2a]/40 transition-all ${className}`}
    >
      <div className="absolute inset-0">
        <img
          src={img}
          alt={title}
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/30" />
      </div>

      {/* Badge */}
      <div className="absolute top-5 left-5 right-5 flex items-start justify-between z-10">
        <div className="inline-flex items-center gap-2 bg-black/60 backdrop-blur-md border border-neutral-800 px-3 py-1.5">
          <span className="w-1.5 h-1.5 bg-[#e11d2a] rounded-full" />
          <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-200">
            {badge}
          </span>
        </div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-300 hidden md:block">
          {tag}
        </div>
      </div>

      {/* Bottom content */}
      <div className="absolute inset-x-0 bottom-0 p-7 md:p-10 z-10">
        <div className="flex items-end justify-between gap-6">
          <div className="flex-1">
            <h3
              className={`font-anton uppercase tracking-tight leading-none ${
                featured
                  ? "text-4xl md:text-6xl"
                  : "text-3xl md:text-4xl"
              }`}
            >
              {title}
            </h3>
            <p className="text-neutral-300 text-sm md:text-base mt-3 max-w-md">
              {subtitle}
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-400 mb-1">
              From
            </div>
            <div className="font-anton text-3xl md:text-4xl text-[#e11d2a]">
              {price}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={onCta}
            data-testid={`${id}-reserve-btn`}
            className="inline-flex items-center gap-2 bg-[#e11d2a] hover:bg-[#ff3340] text-white font-bold uppercase tracking-wide text-xs px-5 py-3 transition-colors"
          >
            Reserve Yours
            <ArrowUpRight size={14} />
          </button>
          <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">
            Ships Pre-Season
          </span>
        </div>
      </div>
    </article>
  );
}
